import { skipToken } from "@reduxjs/toolkit/query";
import { Spin } from "antd";
import { AlertCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetRoutingPublicFormQuery,
  useSubmitRoutingPublicFormMutation,
} from "@/api/scheduling/routing-public.api";
import type {
  RoutingPublicJsonRecord,
  RoutingPublicSubmitData,
} from "@/api/scheduling/routing-public.types";
import { colors } from "@/design-system";
import RoutingFormSubmit from "./components/routing/formSubmit";
import {
  buildRoutingSubmissionHandoff,
  saveRoutingSubmissionHandoff,
} from "./routingSubmissionHandoff";

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== "object") return fallback;
  const data = "data" in error ? (error as { data?: unknown }).data : error;
  if (data && typeof data === "object" && "message" in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if ("message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
};

const PublicRoutingState = ({
  title,
  message,
}: {
  title: string;
  message: string;
}) => (
  <div
    className="mx-auto flex w-full max-w-lg flex-col items-center rounded-lg border px-5 py-8 text-center shadow-sm sm:px-6 sm:py-10"
    style={{
      backgroundColor: colors.white,
      borderColor: colors.surfaceSubtle,
    }}
  >
    <AlertCircle className="h-8 w-8" style={{ color: colors.textMuted }} />
    <h1
      className="mt-4 text-xl font-bold leading-7"
      style={{ color: colors.textDarkest }}
    >
      {title}
    </h1>
    <p className="mt-2 text-sm leading-6" style={{ color: colors.textMuted }}>
      {message}
    </p>
    <img src="/notifyy.png" alt="Notifyy" className="mt-8 h-7 w-auto" />
  </div>
);

const RoutingFormPage = () => {
  const { publicKey } = useParams<{ publicKey?: string }>();
  const navigate = useNavigate();
  const normalizedPublicKey = publicKey?.trim() || "";
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const {
    data: routingForm,
    error: routingFormError,
    isFetching,
    isLoading,
  } = useGetRoutingPublicFormQuery(
    normalizedPublicKey ? { publicKey: normalizedPublicKey } : skipToken,
  );
  const [submitRoutingPublicForm, { isLoading: isSubmitting }] =
    useSubmitRoutingPublicFormMutation();

  const loadErrorMessage = useMemo(
    () =>
      routingFormError
        ? getApiErrorMessage(routingFormError, "Unable to load routing form.")
        : "",
    [routingFormError],
  );

  const handleSubmit = async ({
    answers,
    inviteeName,
    inviteeEmail,
    inviteePhone,
  }: {
    answers: RoutingPublicJsonRecord;
    inviteeName?: string;
    inviteeEmail?: string;
    inviteePhone?: string;
  }): Promise<RoutingPublicSubmitData | null> => {
    if (!normalizedPublicKey) {
      setSubmitError("Routing form public key is required.");
      return null;
    }

    try {
      setSubmitError("");
      setSuccessMessage("");
      const result = await submitRoutingPublicForm({
        publicKey: normalizedPublicKey,
        ...(inviteeName ? { inviteeName } : {}),
        ...(inviteeEmail ? { inviteeEmail } : {}),
        ...(inviteePhone ? { inviteePhone } : {}),
        answers,
        source: "public_routing_form",
      }).unwrap();
      setSuccessMessage(result.message);
      const handoff = buildRoutingSubmissionHandoff(
        normalizedPublicKey,
        result.data,
      );
      const bookingPublicKey = result.data.bookingPublicKey?.trim() || "";
      if (/^evt_[A-Za-z0-9_-]+$/.test(bookingPublicKey)) {
        if (handoff) saveRoutingSubmissionHandoff(handoff);
        navigate(
          `/book/${encodeURIComponent(bookingPublicKey)}`,
          handoff ? { state: { routingSubmission: handoff } } : undefined,
        );
        return result.data;
      }

      const redirectUrl =
        result.data.bookingUrl?.trim() || result.data.targetUrl?.trim();
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return result.data;
      }

      return result.data;
    } catch (error) {
      setSubmitError(
        getApiErrorMessage(error, "Unable to submit routing form."),
      );
      return null;
    }
  };

  return (
    <main
      className="min-h-dvh overflow-y-auto px-3 py-4 sm:px-6 sm:py-8 lg:px-8"
      style={{
        backgroundColor: colors.surfaceLightest,
        color: colors.textDefault,
      }}
    >
      <div className="mx-auto flex min-h-[calc(100dvh-2rem)] w-full items-start justify-center sm:min-h-[calc(100dvh-4rem)] sm:pt-8">
        {!normalizedPublicKey ? (
          <PublicRoutingState
            title="Routing form unavailable"
            message="The routing form link is missing a public key."
          />
        ) : isLoading || isFetching ? (
          <div
            className="mx-auto flex w-full max-w-lg flex-col items-center rounded-lg border px-5 py-10 shadow-sm sm:px-6 sm:py-12"
            style={{
              backgroundColor: colors.white,
              borderColor: colors.surfaceSubtle,
            }}
          >
            <Spin />
            <p
              className="mt-4 text-sm leading-6"
              style={{ color: colors.textMuted }}
            >
              Loading routing form...
            </p>
          </div>
        ) : loadErrorMessage || !routingForm ? (
          <PublicRoutingState
            title="Routing form unavailable"
            message={loadErrorMessage || "This routing form could not be found."}
          />
        ) : (
          <RoutingFormSubmit
            form={routingForm}
            isSubmitting={isSubmitting}
            submitError={submitError}
            successMessage={successMessage}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </main>
  );
};

export default RoutingFormPage;
